/**
 * FlashEventPopup — inspector for a light (flash) event in the Synth VR editor.
 *
 * Opens when a light bulb marker is clicked (store.editingFlashBeat). In-game the
 * light is always white, so there's no colour control; you tune how bright it is
 * (intensity), how long one flash lasts (duration), how many times it blinks
 * (repeat) and — when strobing — how close together (frequency). Edits also
 * update the flash defaults so the next light you place inherits the settings.
 *
 * Uses the same themed controls as the left sidebar (GradientSlider + Stepper-
 * Input) so it matches the rest of the app. Non-modal floating card; close with
 * the ×, Escape, or by opening another light.
 */

import React, { useEffect } from 'react';
import { useEditorStore } from '../../store/editorStore';
import type { FlashEvent } from '../../types/beatmap';
import { color, font } from '../ui/theme';
import { IcTrash, IcBulb } from '../ui/icons';
import GradientSlider from '../ui/GradientSlider';
import StepperInput from '../ui/StepperInput';
import { useDraggablePanel } from '../ui/useDraggablePanel';

const round = (x: number, d: number) => { const f = Math.pow(10, d); return Math.round(x * f) / f; };
const LABEL_W = 62;
// Slider ranges (GradientSlider works in 0..1).
const IMIN = 0.1, IMAX = 2;    // intensity
const DMIN = 0.05, DMAX = 5;   // duration (s)
const FMIN = 0.5, FMAX = 16;   // frequency (Hz)

export default function FlashEventPopup() {
  const editingFlashBeat = useEditorStore(s => s.editingFlashBeat);
  const selectedFlashBeats = useEditorStore(s => s.selectedFlashBeats);
  const flashEvents = useEditorStore(s => s.flashEvents);
  const updateFlashEvents = useEditorStore(s => s.updateFlashEvents);
  const removeFlashEvent = useEditorStore(s => s.removeFlashEvent);
  const setEditingFlashBeat = useEditorStore(s => s.setEditingFlashBeat);
  const setFlashDefaults = useEditorStore(s => s.setFlashDefaults);
  const { panelRef, headerProps, draggedStyle } = useDraggablePanel();

  useEffect(() => {
    if (editingFlashBeat == null) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setEditingFlashBeat(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editingFlashBeat, setEditingFlashBeat]);

  if (editingFlashBeat == null) return null;
  const ev = flashEvents.find(e => Math.abs(e.time_beats - editingFlashBeat) < 0.01);
  if (!ev) return null;

  const intensity = ev.intensity;
  const duration = ev.duration ?? (((ev.fadeInTime || 0) + (ev.fadeOutTime || 0)) || 0.4);
  const repeat = ev.repeat ?? 1;
  const frequency = ev.frequency ?? (1 / Math.max(0.05, duration));
  const interval = 1 / Math.max(0.1, frequency);
  const totalSecs = (repeat - 1) * interval + duration;

  // The popup edits every selected light at once (so they share one config), and
  // remembers the change as the default for the next light placed.
  const targets = selectedFlashBeats.length ? selectedFlashBeats : [ev.time_beats];
  const multi = targets.length > 1;
  const patch = (u: Partial<FlashEvent>) => { updateFlashEvents(targets, u); setFlashDefaults(u); };
  const close = () => setEditingFlashBeat(null);
  const del = () => { [...targets].forEach(b => removeFlashEvent(b)); close(); };
  const setRepeat = (n: number) => patch({ repeat: Math.max(1, Math.min(64, Math.round(n) || 1)) });

  return (
    <div ref={panelRef} style={{ ...panelStyle, ...draggedStyle }}>
      <div style={{ ...headerStyle, ...headerProps.style }} onPointerDown={headerProps.onPointerDown}>
        <span style={titleStyle}>
          <IcBulb size={15} style={{ color: '#fff' }} />
          {multi ? `Lights · ${targets.length} selected` : `Light · beat ${ev.time_beats.toFixed(1)}`}
        </span>
        <button style={closeBtnStyle} onClick={close} title="Close (Esc)">×</button>
      </div>

      <GradientSlider
        inline labelWidth={LABEL_W} label="Intensity"
        value={(intensity - IMIN) / (IMAX - IMIN)}
        onChange={v => patch({ intensity: round(IMIN + v * (IMAX - IMIN), 2) })}
        valueText={intensity.toFixed(2)}
      />

      <GradientSlider
        inline labelWidth={LABEL_W} label="Duration"
        value={(duration - DMIN) / (DMAX - DMIN)}
        onChange={v => patch({ duration: round(DMIN + v * (DMAX - DMIN), 2) })}
        valueText={`${duration.toFixed(2)}s`}
      />

      <StepperInput
        labelWidth={LABEL_W} label="Repeat" hoverAccent
        value={String(repeat)}
        onDecrement={() => setRepeat(repeat - 1)}
        onIncrement={() => setRepeat(repeat + 1)}
        onCommitValue={raw => setRepeat(parseFloat(raw))}
        editTitle="Number of strobe pulses"
      />

      {repeat > 1 && (
        <GradientSlider
          inline labelWidth={LABEL_W} label="Frequency"
          value={(frequency - FMIN) / (FMAX - FMIN)}
          onChange={v => patch({ frequency: round(FMIN + v * (FMAX - FMIN), 1) })}
          valueText={`${frequency.toFixed(1)} Hz`}
        />
      )}

      <div style={hintStyle}>
        {multi && <><b style={{ color: color.ink }}>Editing {targets.length} lights</b> — changes apply to all.<br /></>}
        The in-game light is always white.{' '}
        {repeat > 1
          ? `Strobes on/off ${repeat}× over ${totalSecs.toFixed(1)}s — raise Frequency to pack the flashes closer.`
          : 'Set Repeat > 1 to strobe the light on/off.'}
      </div>

      <button style={deleteBtnStyle} onClick={del}><IcTrash size={13} /> {multi ? `Delete ${targets.length} lights` : 'Delete light'}</button>
    </div>
  );
}

// Matches the sidebar's bottom box: dark surface, accent outline, heavy blur.
const panelStyle: React.CSSProperties = {
  position: 'fixed', top: 76, left: '50%', transform: 'translateX(-50%)', zIndex: 60,
  width: 'min(280px, calc(100vw - 24px))', maxHeight: 'calc(100vh - 90px)', overflowY: 'auto',
  padding: 14, borderRadius: 16,
  background: 'oklch(0.09 calc(0.018 * var(--tint-c)) var(--tint-h) / 0.92)',
  border: '1.5px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.4)',
  boxShadow: '0 12px 40px rgba(0,0,0,0.55)',
  backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
  display: 'flex', flexDirection: 'column', gap: 11, fontFamily: font.sans,
};
const headerStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 };
const titleStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 7, color: color.ink, fontSize: 13, fontWeight: 700, letterSpacing: 0.2 };
const closeBtnStyle: React.CSSProperties = {
  background: 'none', border: 'none', color: color.inkFaint, cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '0 2px',
};
const hintStyle: React.CSSProperties = { color: color.inkFaint, fontSize: 10, lineHeight: 1.45, marginTop: 2 };
const deleteBtnStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  padding: '10px 10px', borderRadius: 999, cursor: 'pointer',
  background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.35)',
  color: '#F87171', fontSize: 12, fontWeight: 600, fontFamily: font.sans,
};
